import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import DealCard from "../../components/DealCard";
import DealDetailModal from "../../components/DealDetailModal";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { TrendingUp, CheckCircle, Clock, Archive, Loader2, ArrowRight } from "lucide-react";

const PREVIEW_COUNT = 5;

const InvestorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deals`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      setDeals(response.data.deals || response.data);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDeals(); }, []);

  const stats = useMemo(() => {
    const activeDeals = deals.filter(d =>
      (d.status === "active" || d.status === "approved") && !d.reserved_by
    );
    const reservedByMe = deals.filter(d => d.reserved_by === user?.user_id && d.status === "reserved");
    const inClosingByMe = deals.filter(d => d.reserved_by === user?.user_id && d.status === "in_closing");
    const closedByMe = deals.filter(d => d.reserved_by === user?.user_id && d.status === "closed");

    return {
      activeCount: activeDeals.length,
      reservedCount: reservedByMe.length,
      inClosingCount: inClosingByMe.length,
      closedCount: closedByMe.length,
    };
  }, [deals, user]);

  const recentDeals = useMemo(() => {
    return deals
      .filter(d => d.status === "active" || d.status === "approved" || d.status === "reserved" || d.status === "in_closing")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, PREVIEW_COUNT);
  }, [deals]);

  if (loading) {
    return (
      <DashboardLayout title="Investor Dashboard">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Investor Dashboard">
      <div className="space-y-6" data-testid="investor-dashboard">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="stat-card" data-testid="stat-active-deals">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Live Deals</CardTitle>
              <div className="stat-card-icon bg-green-500/10"><TrendingUp className="w-5 h-5 text-green-500" /></div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value text-green-500">{stats.activeCount}</div>
              <p className="stat-card-label">Available now</p>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-reserved">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">My Reserved</CardTitle>
              <div className="stat-card-icon bg-blue-500/10"><CheckCircle className="w-5 h-5 text-blue-500" /></div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value text-blue-500">{stats.reservedCount}</div>
              <p className="stat-card-label">Pending documents</p>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-in-closing">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">In Closing</CardTitle>
              <div className="stat-card-icon bg-purple-500/10"><Clock className="w-5 h-5 text-purple-500" /></div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value text-purple-500">{stats.inClosingCount}</div>
              <p className="stat-card-label">Documents complete</p>
            </CardContent>
          </Card>

          <Card className="stat-card" data-testid="stat-closed">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Closed Deals</CardTitle>
              <div className="stat-card-icon bg-orange-500/10"><Archive className="w-5 h-5 text-orange-500" /></div>
            </CardHeader>
            <CardContent>
              <div className="stat-card-value">{stats.closedCount}</div>
              <p className="stat-card-label">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Live Deals Preview */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Recent Live Deals
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => navigate("/dashboard/investor/deals")} data-testid="view-all-deals-btn">
                View All Deals
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardHeader>

          {recentDeals.length === 0 ? (
            <CardContent className="py-8 text-center text-muted-foreground">
              No live deals available yet. Check back soon!
            </CardContent>
          ) : (
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentDeals.map((deal) => (
                  <DealCard key={deal.deal_id} deal={deal} viewMode="grid" onViewDeal={() => setSelectedDeal(deal)} />
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      <DealDetailModal
        deal={selectedDeal}
        open={!!selectedDeal}
        onOpenChange={(open) => !open && setSelectedDeal(null)}
        onDealUpdate={fetchDeals}
      />
    </DashboardLayout>
  );
};

export default InvestorDashboard;
